import React, { useState, useEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { MENU_ITEMS } from '../lib/nav'
import { ArcOrbitButton } from './ArcOrbitButton'

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [activeDropdown, setActiveDropdown] = useState(null)
  const [activeNestedDropdown, setActiveNestedDropdown] = useState(null)
  const [mobileExpanded, setMobileExpanded] = useState({})
  const [mobileNestedExpanded, setMobileNestedExpanded] = useState({})
  const [scrolled, setScrolled] = useState(false)
  const location = useLocation()
  const navRef = useRef(null)
  const dropdownTimeoutRef = useRef(null)
  const nestedTimeoutRef = useRef(null)

  const handleDropdownEnter = (label) => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current)
      dropdownTimeoutRef.current = null
    }
    setActiveDropdown(label)
  }

  const handleDropdownLeave = () => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current)
    }
    dropdownTimeoutRef.current = setTimeout(() => {
      setActiveDropdown(null)
      setActiveNestedDropdown(null)
    }, 280)
  }

  const handleNestedEnter = (label) => {
    if (nestedTimeoutRef.current) {
      clearTimeout(nestedTimeoutRef.current)
      nestedTimeoutRef.current = null
    }
    setActiveNestedDropdown(label)
  }

  const handleNestedLeave = () => {
    if (nestedTimeoutRef.current) {
      clearTimeout(nestedTimeoutRef.current)
    }
    nestedTimeoutRef.current = setTimeout(() => {
      setActiveNestedDropdown(null)
    }, 280)
  }

  // Track scroll for subtle floating header elevation
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20)
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Close mobile menu on route change
  useEffect(() => {
    if (dropdownTimeoutRef.current) clearTimeout(dropdownTimeoutRef.current)
    if (nestedTimeoutRef.current) clearTimeout(nestedTimeoutRef.current)
    setMobileOpen(false)
    setActiveDropdown(null)
    setActiveNestedDropdown(null)
  }, [location.pathname])

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        if (dropdownTimeoutRef.current) clearTimeout(dropdownTimeoutRef.current)
        if (nestedTimeoutRef.current) clearTimeout(nestedTimeoutRef.current)
        setActiveDropdown(null)
        setActiveNestedDropdown(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      if (dropdownTimeoutRef.current) clearTimeout(dropdownTimeoutRef.current)
      if (nestedTimeoutRef.current) clearTimeout(nestedTimeoutRef.current)
    }
  }, [])

  const isCurrentActive = (item) => {
    if (item.external) return false
    if (location.pathname === item.href) return true
    if (
      item.children &&
      item.children.some((sub) => {
        if (!sub.external && location.pathname === sub.href) return true
        if (sub.children && sub.children.some((nested) => !nested.external && location.pathname === nested.href))
          return true
        return false
      })
    ) {
      return true
    }
    if (item.href !== '/' && location.pathname.startsWith(item.href)) {
      return true
    }
    return false
  }

  const toggleMobileSub = (label) => {
    setMobileExpanded((prev) => ({
      ...prev,
      [label]: !prev[label],
    }))
  }

  const toggleMobileNested = (label) => {
    setMobileNestedExpanded((prev) => ({
      ...prev,
      [label]: !prev[label],
    }))
  }

  return (
    <header
      ref={navRef}
      className="floating-header"
      aria-label="Hovednavigation"
    >
      {/* Floating Pill Header Container */}
      <div
        className={`floating-header__inner transition-all duration-300 ${
          scrolled
            ? '!bg-white/85 shadow-[0_12px_36px_rgba(0,0,0,0.14)]'
            : '!bg-white/72 shadow-[0_6px_30px_rgba(0,0,0,0.08)]'
        }`}
      >
        {/* Logo */}
        <Link
          to="/"
          className="flex items-center gap-2 focus:outline-none rounded-full p-1 transition-transform duration-300 hover:scale-[1.03] flex-shrink-0"
          aria-label="Emmerske Efterskole forside"
        >
          <img
            src="https://assets.auxodev.dk/assets/e40f0695-6390-4586-b67e-d6f4d574a533/ee-nylogo-trans.png"
            alt="Emmerske Efterskole"
            className="h-10 sm:h-11 lg:h-12 w-auto object-contain drop-shadow-xs"
            loading="eager"
          />
        </Link>

        {/* Desktop Navigation (>= 1200px) */}
        <nav className="hidden min-[1200px]:flex items-center justify-center flex-1 mx-2 lg:mx-4 gap-1 xl:gap-2">
          {MENU_ITEMS.map((item) => {
            const active = isCurrentActive(item)
            const hasSub = item.children && item.children.length > 0
            const isDropdownOpen = activeDropdown === item.label

            if (item.isCta) {
              return (
                <ArcOrbitButton
                  key={item.label}
                  to={item.href}
                  icon={<i className="fa-solid fa-calendar-check text-xs text-white" aria-hidden="true" />}
                  arrow={false}
                  className="!min-h-[46px] ml-2 shrink-0"
                >
                  {item.label}
                </ArcOrbitButton>
              )
            }

            return (
              <div
                key={item.label}
                className="relative flex-shrink-0"
                onMouseEnter={() => hasSub && handleDropdownEnter(item.label)}
                onMouseLeave={() => hasSub && handleDropdownLeave()}
              >
                {item.external ? (
                  <a
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs xl:text-[13px] 2xl:text-[14px] font-extrabold text-[#36454F] hover:text-[#007A53] transition-colors whitespace-nowrap"
                  >
                    <span>{item.label}</span>
                    <i
                      className="fa-solid fa-arrow-up-right-from-square text-[9px] text-[#2F6F73]/60"
                      aria-hidden="true"
                    />
                  </a>
                ) : (
                  <Link
                    to={item.href}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs xl:text-[13px] 2xl:text-[14px] font-extrabold transition-all whitespace-nowrap rounded-full ${
                      active
                        ? 'text-[#007A53] bg-[#DCEEF2]/60'
                        : 'text-[#36454F] hover:text-[#007A53] hover:bg-white/60'
                    }`}
                    aria-expanded={hasSub ? isDropdownOpen : undefined}
                    onClick={() => {
                      if (hasSub && activeDropdown === item.label) {
                        setActiveDropdown(null)
                      }
                    }}
                  >
                    <span>{item.label}</span>
                    {hasSub && (
                      <i
                        className={`fa-solid fa-chevron-down text-[9px] transition-transform duration-200 ${
                          isDropdownOpen ? 'rotate-180 text-[#007A53]' : 'text-[#36454F]/60'
                        }`}
                        aria-hidden="true"
                      />
                    )}
                  </Link>
                )}

                {/* Dropdown Menu - Floating Panel with Soft Corners */}
                {hasSub && (
                  item.label === 'Undervisning' ? (
                    /* Mega-Panel for 'Undervisning' - 3 Columns with zero clipping or fragile flyouts */
                    <div
                      className={`absolute top-full -left-24 pt-3 w-[760px] transition-all duration-200 transform origin-top-left z-50 ${
                        isDropdownOpen
                          ? 'opacity-100 scale-100 pointer-events-auto visible translate-y-0'
                          : 'opacity-0 scale-95 pointer-events-none invisible -translate-y-2'
                      }`}
                      onMouseEnter={() => handleDropdownEnter(item.label)}
                      onMouseLeave={handleDropdownLeave}
                    >
                      <div className="bg-white rounded-3xl shadow-[0_25px_60px_rgba(47,111,115,0.28)] border border-[#DCEEF2] p-6">
                        <div className="grid grid-cols-3 gap-6">
                          {/* Kolonne 1: Valgfag */}
                          <div className="bg-[#DCEEF2]/40 rounded-2xl p-4 border border-[#DCEEF2]/80 flex flex-col justify-between">
                            <div>
                              <div className="flex items-center justify-between pb-2.5 mb-2.5 border-b border-[#2F6F73]/15">
                                <Link
                                  to="/undervisning/valgfag"
                                  onClick={() => setActiveDropdown(null)}
                                  className="text-sm font-black text-[#007A53] hover:text-[#2F6F73] flex items-center gap-1.5"
                                >
                                  <span>Valgfag</span>
                                  <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                                </Link>
                                <span className="text-[10px] font-bold text-[#2F6F73] bg-white px-2.5 py-0.5 rounded-full shadow-xs">
                                  8 fag
                                </span>
                              </div>
                              <div className="space-y-0.5">
                                {item.children.find((c) => c.label === 'Valgfag')?.children?.map((vf) => {
                                  const vfActive = location.pathname === vf.href
                                  return (
                                    <Link
                                      key={vf.label}
                                      to={vf.href}
                                      onClick={() => setActiveDropdown(null)}
                                      className={`block px-2.5 py-1 rounded-lg text-xs transition-colors ${
                                        vfActive
                                          ? 'bg-[#007A53] text-white font-extrabold'
                                          : 'text-[#36454F] hover:bg-white hover:text-[#007A53] font-semibold'
                                      }`}
                                    >
                                      {vf.label}
                                    </Link>
                                  )
                                })}
                              </div>
                            </div>
                            <Link
                              to="/undervisning/valgfag"
                              onClick={() => setActiveDropdown(null)}
                              className="mt-3.5 text-xs font-black text-[#007A53] hover:text-[#2F6F73] flex items-center justify-between pt-2.5 border-t border-[#2F6F73]/15"
                            >
                              <span>Se alle valgfag</span>
                              <i className="fa-solid fa-chevron-right text-[9px]" aria-hidden="true" />
                            </Link>
                          </div>

                          {/* Kolonne 2: Fag */}
                          <div className="bg-[#DCEEF2]/40 rounded-2xl p-4 border border-[#DCEEF2]/80 flex flex-col justify-between">
                            <div>
                              <div className="flex items-center justify-between pb-2.5 mb-2.5 border-b border-[#2F6F73]/15">
                                <Link
                                  to="/undervisning/fag"
                                  onClick={() => setActiveDropdown(null)}
                                  className="text-sm font-black text-[#007A53] hover:text-[#2F6F73] flex items-center gap-1.5"
                                >
                                  <span>Fag</span>
                                  <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                                </Link>
                                <span className="text-[10px] font-bold text-[#2F6F73] bg-white px-2.5 py-0.5 rounded-full shadow-xs">
                                  Skolefag
                                </span>
                              </div>
                              <div className="space-y-0.5">
                                {item.children.find((c) => c.label === 'Fag')?.children?.map((f) => {
                                  const fActive = location.pathname === f.href
                                  return (
                                    <Link
                                      key={f.label}
                                      to={f.href}
                                      onClick={() => setActiveDropdown(null)}
                                      className={`block px-2.5 py-1 rounded-lg text-xs transition-colors ${
                                        fActive
                                          ? 'bg-[#007A53] text-white font-extrabold'
                                          : 'text-[#36454F] hover:bg-white hover:text-[#007A53] font-semibold'
                                      }`}
                                    >
                                      {f.label}
                                    </Link>
                                  )
                                })}
                              </div>
                            </div>
                            <Link
                              to="/undervisning/fag"
                              onClick={() => setActiveDropdown(null)}
                              className="mt-3.5 text-xs font-black text-[#007A53] hover:text-[#2F6F73] flex items-center justify-between pt-2.5 border-t border-[#2F6F73]/15"
                            >
                              <span>Se fagoversigt</span>
                              <i className="fa-solid fa-chevron-right text-[9px]" aria-hidden="true" />
                            </Link>
                          </div>

                          {/* Kolonne 3: Særlige forløb & Overblik */}
                          <div className="flex flex-col justify-between">
                            <div>
                              <div className="pb-2 mb-2 border-b border-[#2F6F73]/15">
                                <span className="text-sm font-black text-[#2F6F73]">
                                  Forløb & redskaber
                                </span>
                              </div>
                              <div className="space-y-1.5">
                                {item.children
                                  .filter((c) => c.label !== 'Valgfag' && c.label !== 'Fag')
                                  .map((sub) => {
                                    const subActive = location.pathname === sub.href
                                    return (
                                      <Link
                                        key={sub.label}
                                        to={sub.href}
                                        onClick={() => setActiveDropdown(null)}
                                        className={`block px-3 py-2.5 rounded-xl text-xs sm:text-[13px] font-bold transition-all ${
                                          subActive
                                            ? 'bg-[#007A53] text-white shadow-sm'
                                            : 'bg-white hover:bg-[#DCEEF2] text-[#36454F] hover:text-[#007A53] border border-[#DCEEF2]'
                                        }`}
                                      >
                                        <div className="flex items-center justify-between">
                                          <span>{sub.label}</span>
                                          <i className="fa-solid fa-chevron-right text-[9px] opacity-60" aria-hidden="true" />
                                        </div>
                                      </Link>
                                    )
                                  })}
                              </div>
                            </div>

                            {/* Genvej / Infoboks */}
                            <Link
                              to="/undervisning"
                              onClick={() => setActiveDropdown(null)}
                              className="mt-4 p-4 bg-gradient-to-br from-[#2F6F73] to-[#007A53] text-white rounded-2xl block group hover:shadow-lg transition-all"
                            >
                              <span className="text-[11px] font-medium text-[#DCEEF2] block mb-0.5">
                                Undervisning for ordblinde
                              </span>
                              <span className="text-xs font-black flex items-center justify-between">
                                <span>Gå til hovedsiden</span>
                                <i className="fa-solid fa-arrow-right group-hover:translate-x-1 transition-transform text-[11px]" aria-hidden="true" />
                              </span>
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div
                      className={`absolute top-full left-0 pt-3 w-64 transition-all duration-200 transform origin-top-left z-50 ${
                        isDropdownOpen
                          ? 'opacity-100 scale-100 pointer-events-auto visible translate-y-0'
                          : 'opacity-0 scale-95 pointer-events-none invisible -translate-y-2'
                      }`}
                      onMouseEnter={() => handleDropdownEnter(item.label)}
                      onMouseLeave={handleDropdownLeave}
                    >
                      <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-[0_15px_35px_rgba(47,111,115,0.16)] border border-[#DCEEF2] p-2.5">
                      <div className="space-y-1 max-h-[75vh] overflow-y-auto py-1">
                        {item.children.map((sub) => {
                          const subActive = location.pathname === sub.href
                          const hasNested = sub.children && sub.children.length > 0
                          const isNestedActive =
                            hasNested && sub.children.some((c) => c.href === location.pathname)

                          if (sub.external) {
                            return (
                              <a
                                key={sub.label}
                                href={sub.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group block px-3 py-2 rounded-xl transition-all duration-200 hover:bg-[#DCEEF2]/60 text-[#36454F] hover:text-[#007A53]"
                                onClick={() => {
                                  setActiveDropdown(null)
                                  setActiveNestedDropdown(null)
                                }}
                              >
                                <div className="flex items-center justify-between">
                                  <span className="text-xs sm:text-[13px] font-bold">
                                    {sub.label}
                                  </span>
                                  <i
                                    className="fa-solid fa-arrow-up-right-from-square text-[9px] text-[#2F6F73]/50 group-hover:text-[#007A53]"
                                    aria-hidden="true"
                                  />
                                </div>
                              </a>
                            )
                          }

                          if (hasNested) {
                            const isNestedOpen = activeNestedDropdown === sub.label

                            return (
                              <div
                                key={sub.label}
                                className="relative group/nested"
                                onMouseEnter={() => handleNestedEnter(sub.label)}
                                onMouseLeave={handleNestedLeave}
                              >
                                <Link
                                  to={sub.href}
                                  className={`block px-3 py-2 rounded-xl transition-all duration-200 ${
                                    subActive || isNestedActive
                                      ? 'bg-[#DCEEF2] text-[#007A53] font-black'
                                      : 'hover:bg-[#DCEEF2]/50 text-[#36454F] hover:text-[#007A53]'
                                  }`}
                                  onClick={() => {
                                    setActiveDropdown(null)
                                    setActiveNestedDropdown(null)
                                  }}
                                >
                                  <div className="flex items-center justify-between">
                                    <span
                                      className={`text-xs sm:text-[13px] ${
                                        subActive || isNestedActive
                                          ? 'font-black text-[#007A53]'
                                          : 'font-bold text-[#36454F]'
                                      }`}
                                    >
                                      {sub.label}
                                    </span>
                                    <i
                                      className={`fa-solid fa-chevron-right text-[9px] transition-transform duration-200 ${
                                        isNestedOpen ? 'translate-x-1 text-[#007A53]' : 'text-[#36454F]/50'
                                      }`}
                                      aria-hidden="true"
                                    />
                                  </div>
                                </Link>

                                {/* Nested Submenu Panel */}
                                <div
                                  className={`absolute left-full top-0 pl-2 w-64 transition-all duration-200 transform origin-top-left z-50 ${
                                    isNestedOpen
                                      ? 'opacity-100 scale-100 pointer-events-auto visible translate-x-0'
                                      : 'opacity-0 scale-95 pointer-events-none invisible -translate-x-2'
                                  }`}
                                  onMouseEnter={() => handleNestedEnter(sub.label)}
                                  onMouseLeave={handleNestedLeave}
                                >
                                  <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-[0_15px_35px_rgba(47,111,115,0.18)] border border-[#DCEEF2] p-2.5">
                                    <div className="space-y-1 max-h-[70vh] overflow-y-auto py-1">
                                      {sub.children.map((nested) => {
                                        const nestedActive = location.pathname === nested.href

                                        return (
                                          <Link
                                            key={nested.label}
                                            to={nested.href}
                                            className={`block px-3 py-2 rounded-xl transition-all duration-200 ${
                                              nestedActive
                                                ? 'bg-[#DCEEF2] text-[#007A53] font-black'
                                                : 'hover:bg-[#DCEEF2]/50 text-[#36454F] hover:text-[#007A53]'
                                            }`}
                                            onClick={() => {
                                              setActiveDropdown(null)
                                              setActiveNestedDropdown(null)
                                            }}
                                          >
                                            <div className="flex items-center justify-between">
                                              <span
                                                className={`text-xs sm:text-[13px] ${
                                                  nestedActive
                                                    ? 'font-black text-[#007A53]'
                                                    : 'font-bold text-[#36454F]'
                                                }`}
                                              >
                                                {nested.label}
                                              </span>
                                              <i
                                                className={`fa-solid fa-arrow-right text-[10px] transition-all duration-200 ${
                                                  nestedActive
                                                    ? 'text-[#007A53] translate-x-1 opacity-100'
                                                    : 'text-[#2F6F73]/40 group-hover:text-[#007A53] group-hover:translate-x-1 opacity-0 group-hover:opacity-100'
                                                }`}
                                                aria-hidden="true"
                                              />
                                            </div>
                                          </Link>
                                        )
                                      })}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )
                          }

                          return (
                            <Link
                              key={sub.label}
                              to={sub.href}
                              className={`group block px-3 py-2 rounded-xl transition-all duration-200 ${
                                subActive
                                  ? 'bg-[#DCEEF2] text-[#007A53] font-black'
                                  : 'hover:bg-[#DCEEF2]/50 text-[#36454F] hover:text-[#007A53]'
                              }`}
                              onClick={() => {
                                setActiveDropdown(null)
                                setActiveNestedDropdown(null)
                              }}
                            >
                              <div className="flex items-center justify-between">
                                <span
                                  className={`text-xs sm:text-[13px] ${
                                    subActive
                                      ? 'font-black text-[#007A53]'
                                      : 'font-bold text-[#36454F]'
                                  }`}
                                >
                                  {sub.label}
                                </span>
                                <i
                                  className={`fa-solid fa-arrow-right text-[10px] transition-all duration-200 ${
                                    subActive
                                      ? 'text-[#007A53] translate-x-1 opacity-100'
                                      : 'text-[#2F6F73]/40 group-hover:text-[#007A53] group-hover:translate-x-1 opacity-0 group-hover:opacity-100'
                                  }`}
                                  aria-hidden="true"
                                />
                              </div>
                            </Link>
                          )
                        })}
                      </div>
                    </div>
                  </div>
                  )
                )}
              </div>
            )
          })}
        </nav>

        {/* Mobile Round Hamburger Button (< 1200px) */}
        <div className="flex min-[1200px]:hidden items-center gap-2">
          <Link
            to="/rundvisning"
            className="hidden sm:inline-flex items-center gap-1.5 text-xs font-extrabold text-white bg-[#007A53] hover:bg-[#005F40] px-3.5 py-2 rounded-full shadow-sm transition-colors"
          >
            <span>Rundvisning</span>
          </Link>
          <button
            type="button"
            onClick={() => setMobileOpen(!mobileOpen)}
            className="h-10 w-10 sm:h-11 sm:w-11 rounded-full bg-[#007A53] hover:bg-[#005F40] text-white flex items-center justify-center shadow-md transition-colors duration-200 cursor-pointer focus:outline-none"
            aria-label={mobileOpen ? 'Luk menu' : 'Åbn menu'}
            aria-expanded={mobileOpen}
          >
            <i
              className={`fa-solid ${mobileOpen ? 'fa-xmark' : 'fa-bars'} text-lg`}
              aria-hidden="true"
            />
          </button>
        </div>
      </div>

      {/* Mobile Floating Menu Panel Under Header (< 1200px) */}
      {mobileOpen && (
        <div
          className="min-[1200px]:hidden mt-2.5 w-full bg-white/95 backdrop-blur-2xl rounded-3xl border border-white/80 shadow-[0_20px_50px_rgba(47,111,115,0.22)] p-4 sm:p-6 max-h-[82vh] overflow-y-auto animate-in fade-in slide-in-from-top-3 duration-300"
          role="dialog"
          aria-label="Mobilmenu"
        >
          {/* Top Quick Action */}
          <div className="mb-4 pb-3 border-b border-[#DCEEF2]">
            <ArcOrbitButton
              to="/rundvisning"
              onClick={() => setMobileOpen(false)}
              icon={<i className="fa-solid fa-calendar-check text-white" aria-hidden="true" />}
              className="w-full !min-h-[52px]"
            >
              Book en personlig rundvisning
            </ArcOrbitButton>
          </div>

          {/* Navigation Links */}
          <div className="space-y-1.5">
            {MENU_ITEMS.map((item) => {
              const active = isCurrentActive(item)
              const hasSub = item.children && item.children.length > 0
              const isExpanded = Boolean(mobileExpanded[item.label])

              if (item.isCta) return null

              return (
                <div key={item.label} className="border-b border-[#DCEEF2]/60 last:border-b-0 pb-1.5">
                  <div className="flex items-center justify-between">
                    {item.external ? (
                      <a
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="py-2.5 px-3 text-base font-extrabold text-[#36454F] hover:text-[#007A53] flex items-center gap-2"
                        onClick={() => setMobileOpen(false)}
                      >
                        <span>{item.label}</span>
                        <i className="fa-solid fa-arrow-up-right-from-square text-xs text-[#2F6F73]/50" />
                      </a>
                    ) : (
                      <Link
                        to={item.href}
                        onClick={() => setMobileOpen(false)}
                        className={`py-2.5 px-3 text-base font-extrabold transition-colors flex-1 ${
                          active ? 'text-[#007A53]' : 'text-[#36454F] hover:text-[#007A53]'
                        }`}
                      >
                        {item.label}
                      </Link>
                    )}

                    {hasSub && (
                      <button
                        type="button"
                        onClick={() => toggleMobileSub(item.label)}
                        className="p-2.5 text-[#36454F] hover:text-[#007A53] transition-colors cursor-pointer"
                        aria-label={`Udvid ${item.label} menu`}
                      >
                        <i
                          className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${
                            isExpanded ? 'rotate-180 text-[#007A53]' : ''
                          }`}
                        />
                      </button>
                    )}
                  </div>

                  {/* Submenu Accordion */}
                  {hasSub && isExpanded && (
                    <div className="ml-3 pl-3 border-l-2 border-[#007A53]/30 space-y-1 mt-1 pb-2">
                      {item.children.map((sub) => {
                        const subActive = location.pathname === sub.href
                        const hasNested = sub.children && sub.children.length > 0
                        const isNestedExpanded = Boolean(
                          mobileNestedExpanded[`${item.label}_${sub.label}`]
                        )

                        if (sub.external) {
                          return (
                            <a
                              key={sub.label}
                              href={sub.href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center justify-between py-2 px-2 text-sm font-bold text-[#36454F] hover:text-[#007A53]"
                              onClick={() => setMobileOpen(false)}
                            >
                              <span>{sub.label}</span>
                              <i className="fa-solid fa-arrow-up-right-from-square text-[10px] text-[#2F6F73]/40" />
                            </a>
                          )
                        }

                        return (
                          <div key={sub.label}>
                            <div className="flex items-center justify-between">
                              <Link
                                to={sub.href}
                                onClick={() => setMobileOpen(false)}
                                className={`py-1.5 px-2 text-sm transition-colors flex-1 ${
                                  subActive
                                    ? 'text-[#007A53] font-black'
                                    : 'font-bold text-[#36454F] hover:text-[#007A53]'
                                }`}
                              >
                                {sub.label}
                              </Link>
                              {hasNested && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    toggleMobileNested(`${item.label}_${sub.label}`)
                                  }
                                  className="p-1.5 text-[#36454F]"
                                  aria-label={`Udvid ${sub.label}`}
                                >
                                  <i
                                    className={`fa-solid fa-chevron-down text-[10px] transition-transform ${
                                      isNestedExpanded ? 'rotate-180 text-[#007A53]' : ''
                                    }`}
                                  />
                                </button>
                              )}
                            </div>

                            {/* Nested level */}
                            {hasNested && isNestedExpanded && (
                              <div className="ml-3 pl-2.5 border-l border-[#007A53]/20 space-y-1 my-1">
                                {sub.children.map((nested) => {
                                  const nestedActive = location.pathname === nested.href
                                  return (
                                    <Link
                                      key={nested.label}
                                      to={nested.href}
                                      onClick={() => setMobileOpen(false)}
                                      className={`block py-1 px-2 text-xs transition-colors ${
                                        nestedActive
                                          ? 'text-[#007A53] font-black'
                                          : 'font-semibold text-[#536363] hover:text-[#007A53]'
                                      }`}
                                    >
                                      {nested.label}
                                    </Link>
                                  )
                                })}
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          {/* Contact quick links in mobile menu */}
          <div className="mt-5 pt-4 border-t border-[#DCEEF2] space-y-2 text-xs text-[#536363]">
            <p className="flex items-center gap-2">
              <i className="fa-solid fa-phone text-[#007A53]" />
              <a href="tel:74724433" className="font-bold text-[#36454F] hover:text-[#007A53]">
                74 72 44 33
              </a>
            </p>
            <p className="flex items-center gap-2">
              <i className="fa-solid fa-envelope text-[#007A53]" />
              <a href="mailto:ee@eeskole.dk" className="font-bold text-[#36454F] hover:text-[#007A53]">
                ee@eeskole.dk
              </a>
            </p>
          </div>
        </div>
      )}
    </header>
  )
}
